import type { LinkProps } from '@tanstack/react-router'
import {
  Archive,
  BookText,
  Building2,
  ChartColumn,
  ClipboardList,
  HandCoins,
  LayoutDashboard,
  PiggyBank,
  Settings,
  MessageSquare,
  ShieldCheck,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

import { PERMISSIONS, type PermissionCode } from '@/lib/permissions'

export interface NavItem {
  label: string
  /** Typed against the registered route tree, so a menu can never point at a route that is gone. */
  to: LinkProps['to']
  icon: LucideIcon
  /** Hidden unless the user holds this permission. */
  permission?: PermissionCode
  /** Hidden unless the user holds at least one of these — for sections spanning several modules. */
  anyOf?: readonly PermissionCode[]
  children?: NavItem[]
}

export interface NavSection {
  label: string
  items: NavItem[]
}

/**
 * The module map, in the order a working day moves through it: who you serve, what they hold,
 * the cash that moves, the controls over it, and finally administration.
 *
 * Items disappear when the user lacks the permission named here. That is a courtesy — fewer dead
 * ends for a teller who will never approve a journal — and nothing more: the server authorises
 * every request against the live catalogue, so a hidden item is not a closed door and a visible
 * one is not an open one.
 */
export const NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: '/', icon: LayoutDashboard }],
  },
  {
    label: 'Customers',
    items: [
      { label: 'Customers', to: '/customers', icon: Users, permission: PERMISSIONS.customers.view },
      { label: 'Groups', to: '/groups', icon: UsersRound, permission: PERMISSIONS.groups.view },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { label: 'Loans', to: '/loans', icon: HandCoins, permission: PERMISSIONS.loans.view },
      { label: 'Savings', to: '/savings', icon: PiggyBank, permission: PERMISSIONS.savings.view },
    ],
  },
  {
    label: 'Cash',
    items: [
      { label: 'Teller', to: '/teller', icon: Wallet, permission: PERMISSIONS.teller.depositPost },
      {
        label: 'Collection capture',
        to: '/collections',
        icon: ClipboardList,
        permission: PERMISSIONS.groups.meetingRecord,
      },
    ],
  },
  {
    label: 'Control',
    items: [
      { label: 'Approvals', to: '/approvals', icon: ShieldCheck },
      {
        label: 'Messages',
        to: '/notifications',
        icon: MessageSquare,
        permission: PERMISSIONS.notifications.outboxView,
      },
      { label: 'Documents', to: '/documents', icon: Archive, permission: PERMISSIONS.documents.view },
      { label: 'Ledger', to: '/ledger', icon: BookText, permission: PERMISSIONS.ledger.journalView },
    ],
  },
  {
    label: 'Insight',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: ChartColumn,
        anyOf: [
          PERMISSIONS.reports.parView,
          PERMISSIONS.reports.collectionsView,
          PERMISSIONS.reports.officerPerformanceView,
          PERMISSIONS.reports.groupHealthView,
          PERMISSIONS.reports.portfolioView,
          PERMISSIONS.reports.trialBalanceView,
        ],
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'Administration',
        to: '/admin',
        icon: Settings,
        anyOf: [
          PERMISSIONS.admin.userView,
          PERMISSIONS.admin.roleView,
          PERMISSIONS.admin.roleManage,
          PERMISSIONS.admin.branchView,
          PERMISSIONS.loans.productManage,
          PERMISSIONS.admin.parameterManage,
        ],
        children: [
          { label: 'Users', to: '/admin/users', icon: Users, permission: PERMISSIONS.admin.userView },
          { label: 'Roles', to: '/admin/roles', icon: ShieldCheck, permission: PERMISSIONS.admin.roleView },
          {
            label: 'Branches',
            to: '/admin/branches',
            icon: Building2,
            // The screen itself only reads `GET /access/me/branches`, which needs no permission at
            // all — `branchView` is this client's own scoping choice to keep the item under
            // Administration consistent with the module's permission story. See `branches.tsx`.
            permission: PERMISSIONS.admin.branchView,
          },
        ],
      },
    ],
  },
]
